import formConfig from '../ir-form-config.json';
import type { FieldDef, SectionDef, TemplateGroup, RefDataItem } from '../../shared/composables/useFormRenderer';
import { useFormRenderer } from '../../shared/composables/useFormRenderer';

export type { FieldDef, SectionDef, TemplateGroup, RefDataItem };

// ===== Composable =====
export function useFormConfig() {
  const { sharedRefData, sharedTemplates, resolveTemplate: _resolveTemplate, resolveGroup: _resolveGroup, getRefOptions } = useFormRenderer();

  const sections = formConfig.sections as SectionDef[];
  // Merge: shared templates as base, IR-specific templates override
  const irTemplates = formConfig.templates as Record<string, FieldDef[]>;
  const templates: Record<string, FieldDef[]> = { ...sharedTemplates, ...irTemplates };
  const validation = formConfig.validation as Record<string, { pattern: string; message: string }>;

  const refData: Record<string, RefDataItem[]> = { ...sharedRefData };

  /**
   * Resolve template ref to field array
   */
  function resolveTemplate(ref: string, includeOptional?: boolean): FieldDef[] {
    return _resolveTemplate(templates, ref, includeOptional);
  }

  /**
   * Resolve a template group into fields
   */
  function resolveGroup(group: TemplateGroup): FieldDef[] {
    return _resolveGroup(templates, group);
  }

  /**
   * Get a specific field definition from a section
   */
  function getFieldById(sectionId: string, fieldId: string): FieldDef | undefined {
    const section = sections.find(s => s.id === sectionId);
    if (!section?.fields) return undefined;
    return section.fields.find(f => f.id === fieldId);
  }

  /**
   * Look up ID type config by value
   */
  function getIdTypeConfig(idTypeValue: string): RefDataItem | undefined {
    return refData.idTypes.find(t => t.value === idTypeValue);
  }

  return {
    formConfig,
    sections,
    templates,
    validation,
    refData,
    resolveTemplate,
    resolveGroup,
    getRefOptions,
    getFieldById,
    getIdTypeConfig,
  };
}
